import React, { useEffect, useState } from "react";
import api, { formatTL, formatDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveQuery, invalidate, TAGS } from "@/lib/liveSync";
import CorporateQuotaPayments from "@/components/CorporateQuotaPayments";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plate } from "@/components/Plate";
import { toast } from "sonner";
import { Money, CreditCard, Bank, CheckCircle, Warning, XCircle, Plus, ArrowClockwise, Trash } from "@phosphor-icons/react";

const METHODS = [
  { key: "cash", label: "Nakit", icon: Money, color: "green" },
  { key: "card", label: "Kart", icon: CreditCard, color: "blue" },
  { key: "bank_transfer", label: "EFT / Havale", icon: Bank, color: "purple" },
];

const METHOD_LABEL = {
  cash: "Nakit", card: "Kart", bank_transfer: "EFT/Havale", wallet: "Cüzdan",
};

const STATUS_UI = {
  paid_this_month: { label: "Bu Ay Ödedi", icon: CheckCircle, cls: "bg-green-100 text-green-800 border-green-300" },
  // Feb 2026: `active_prior` = abonelik aktif ama bu ay için henüz ödeme
  // alınmamış. Bu satırda "Ödeme Al" butonu görünür. Süresi dolmuş bir
  // durum artık YOK — abonelik sadece super_admin sildiğinde biter.
  active_prior: { label: "Ödeme Bekleniyor", icon: Warning, cls: "bg-amber-100 text-amber-800 border-amber-300" },
  not_active: { label: "Aktif Değil", icon: XCircle, cls: "bg-gray-100 text-gray-700 border-gray-300" },
};

function monthLabel(m) {
  const [y, mo] = m.split("-").map(Number);
  const names = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                 "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
  return `${names[mo - 1]} ${y}`;
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function SubscriptionPayments() {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === "super_admin";
  const [month, setMonth] = useState(currentMonth());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRow, setDialogRow] = useState(null);
  const [form, setForm] = useState({ plate: "", plan_id: "", amount: "", method: "cash", start: "", end: "" });

  // Live queries - auto-refresh on focus, interval, and cross-tab invalidation
  const { data: status, refetch: refetchStatus } = useLiveQuery(
    [TAGS.SUBSCRIPTION_PAYMENTS, TAGS.PLATE_SUBSCRIPTIONS],
    async () => (await api.get(`/subscription-payments/monthly-status?month=${month}`)).data,
    { deps: [month] },
  );
  const { data: summary, refetch: refetchSummary } = useLiveQuery(
    TAGS.SUBSCRIPTION_PAYMENTS,
    async () => (await api.get("/subscription-payments/summary?period=monthly")).data,
  );
  const { data: payments, refetch: refetchPayments } = useLiveQuery(
    TAGS.SUBSCRIPTION_PAYMENTS,
    async () => (await api.get(`/subscription-payments?month=${month}`)).data,
    { deps: [month] },
  );
  const { data: plans, refetch: refetchPlans } = useLiveQuery(
    TAGS.PLANS,
    async () => (await api.get("/plans")).data,
  );

  const reloadAll = () => {
    refetchStatus(); refetchSummary(); refetchPayments(); refetchPlans();
  };

  const deletePayment = async (p) => {
    if (!window.confirm(
      `Bu abonelik tahsilatı kaydını silmek istediğinize emin misiniz?\n\n` +
      `Plaka: ${p.plate}\nTutar: ${formatTL(p.amount)}\nYöntem: ${METHOD_LABEL[p.method] || p.method}\n\n` +
      `Bu işlem geri alınamaz.`
    )) return;
    try {
      await api.delete(`/subscription-payments/${p.id}`);
      toast.success("Ödeme kaydı silindi");
      invalidate([TAGS.SUBSCRIPTION_PAYMENTS, TAGS.KASA]);
      reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const openPaymentDialog = async (row) => {
    // Always fetch the latest plans first so the dialog shows current prices
    let currentPlans = plans || [];
    try {
      const { data } = await api.get("/plans");
      currentPlans = data;
    } catch (_e) { /* fall back to state */ }

    const nextStart = new Date();
    const findPlan = (id) => currentPlans.find((p) => p.id === id);
    if (row) {
      const planId = row.latest_subscription?.plan_id || currentPlans[0]?.id || "";
      const plan = findPlan(planId) || currentPlans[0];
      const durDays = Number(plan?.duration_days || 30);
      const nextEnd = new Date();
      nextEnd.setDate(nextEnd.getDate() + durDays);
      setForm({
        plate: row.plate,
        plan_id: planId,
        amount: plan?.price ?? "",
        method: "cash",
        start: nextStart.toISOString().slice(0, 10),
        end: nextEnd.toISOString().slice(0, 10),
      });
    } else {
      const plan = currentPlans[0];
      const durDays = Number(plan?.duration_days || 30);
      const nextEnd = new Date();
      nextEnd.setDate(nextEnd.getDate() + durDays);
      setForm({
        plate: "", plan_id: plan?.id || "", amount: plan?.price ?? "",
        method: "cash",
        start: nextStart.toISOString().slice(0, 10),
        end: nextEnd.toISOString().slice(0, 10),
      });
    }
    setDialogRow(row);
    setDialogOpen(true);
  };

  // Auto-sync amount when user changes plan in dialog
  const onPlanChange = (planId) => {
    const p = (plans || []).find((x) => x.id === planId);
    if (!p) { setForm((f) => ({ ...f, plan_id: planId })); return; }
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + Number(p.duration_days || 30));
    setForm((f) => ({
      ...f,
      plan_id: planId,
      amount: p.price,
      end: end.toISOString().slice(0, 10),
      start: start.toISOString().slice(0, 10),
    }));
  };

  const submitPayment = async () => {
    try {
      await api.post("/subscription-payments", {
        plate: form.plate,
        plan_id: form.plan_id,
        amount: Number(form.amount),
        method: form.method,
        period_start: form.start + "T00:00:00+00:00",
        period_end: form.end + "T23:59:59+00:00",
      });
      toast.success("Abonelik ödemesi kaydedildi");
      setDialogOpen(false); setDialogRow(null);
      // Interceptor already triggered invalidation, but broadcast explicitly
      // for anything else that watches these tags.
      invalidate([TAGS.SUBSCRIPTION_PAYMENTS, TAGS.PLATE_SUBSCRIPTIONS, TAGS.KASA]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const counts = status?.counts || {};

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Abonelik Ödemeleri</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Aylık Ödeme Durumu</h1>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="rounded-sm font-mono w-40" data-testid="subpay-month-picker" />
          <Button variant="outline" onClick={reloadAll}
                  data-testid="subpay-refresh-btn"
                  className="rounded-sm uppercase tracking-widest text-xs"
                  title="Verileri yenile">
            <ArrowClockwise size={14} />
          </Button>
          <Button onClick={() => openPaymentDialog(null)}
                  data-testid="subpay-new-btn"
                  className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
            <Plus size={16} /> Yeni Ödeme
          </Button>
        </div>
      </div>

      {/* Total revenue this month */}
      <Card className="rounded-sm border-primary/40 bg-primary/5">
        <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
              {monthLabel(month)} — Abonelik Tahsilatı
            </div>
            <div className="text-5xl font-bold font-mono text-primary mt-2" data-testid="subpay-month-total">
              {formatTL(status?.total_amount_this_month)}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {counts.paid_this_month ?? 0} ödeme · {(payments || []).length} işlem
            </div>
          </div>
          {summary && (
            <div className="grid grid-cols-3 gap-4 min-w-[320px]">
              {METHODS.map((m) => (
                <div key={m.key}>
                  <div className={`text-[10px] uppercase tracking-widest text-${m.color}-700`}>{m.label}</div>
                  <div className={`text-sm font-mono font-bold text-${m.color}-700`}>
                    {formatTL(summary.by_method[m.key])}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(STATUS_UI).map(([key, ui]) => {
          const Icon = ui.icon;
          return (
            <Card key={key} className={`rounded-sm border ${ui.cls.split(" ").filter(c => c.startsWith("border-")).join(" ")}`}>
              <CardContent className={`p-4 ${ui.cls.split(" ").filter(c => !c.startsWith("border-")).join(" ")}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={18} weight="fill" />
                  <div className="text-xs uppercase tracking-widest font-semibold">{ui.label}</div>
                </div>
                <div className="text-3xl font-bold font-mono" data-testid={`subpay-count-${key}`}>{counts[key] ?? 0}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Monthly status table */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-semibold uppercase tracking-widest">
              Abone Listesi — {monthLabel(month)}
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plaka</TableHead>
                <TableHead>Müşteri</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Son Plan</TableHead>
                <TableHead>Ödeme Tarihi</TableHead>
                <TableHead>Tutar</TableHead>
                <TableHead>Yöntem</TableHead>
                <TableHead>Kapsanan Dönem</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(status?.rows || []).length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  Bu ay için abone yok
                </TableCell></TableRow>
              )}
              {(status?.rows || []).map((r) => {
                const ui = STATUS_UI[r.status];
                const Icon = ui.icon;
                const p = r.payment_this_month;
                return (
                  <TableRow key={r.plate} data-testid={`subpay-row-${r.plate}`}>
                    <TableCell><Plate>{r.plate}</Plate></TableCell>
                    <TableCell className="text-xs" data-testid={`subpay-customer-${r.plate}`}>
                      {r.customer_name ? (
                        <div>
                          <div className="font-medium">{r.customer_name}</div>
                          {r.customer_phone && (
                            <div className="text-[10px] text-muted-foreground font-mono">
                              {r.customer_phone}
                            </div>
                          )}
                        </div>
                      ) : r.corporate_name ? (
                        <span className="text-muted-foreground italic">{r.corporate_name}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-sm text-[10px] uppercase tracking-widest ${ui.cls}`}>
                        <Icon size={10} className="mr-1" /> {ui.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{r.latest_subscription?.plan_name || "—"}</TableCell>
                    <TableCell className="text-xs">{p ? formatDateTime(p.paid_at) : "—"}</TableCell>
                    <TableCell className="font-mono">{p ? formatTL(p.amount) : "—"}</TableCell>
                    <TableCell className="text-xs uppercase">{p ? (METHOD_LABEL[p.method] || p.method) : "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {p ? `${p.period_start.slice(0, 10)} → ${p.period_end.slice(0, 10)}` : "—"}
                    </TableCell>
                    <TableCell>
                      {r.status !== "paid_this_month" && (
                        <Button size="sm" variant="outline" onClick={() => openPaymentDialog(r)}
                                data-testid={`subpay-collect-${r.plate}`}
                                className="rounded-sm text-xs uppercase tracking-widest font-semibold">
                          Ödeme Al
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CorporateQuotaPayments month={month} onChanged={reloadAll} />

      {/* All payments for the month */}
      {(payments || []).length > 0 && (
        <Card className="rounded-sm border-border">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border">
              <h3 className="text-sm font-semibold uppercase tracking-widest">
                {monthLabel(month)} — Tüm Ödemeler
              </h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zaman</TableHead>
                  <TableHead>Plaka</TableHead>
                  <TableHead>Müşteri</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Tutar</TableHead>
                  <TableHead>Yöntem</TableHead>
                  <TableHead>Alan Operatör</TableHead>
                  {isSuperAdmin && <TableHead className="w-16 text-right">İşlem</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(payments || []).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-xs">{formatDateTime(p.paid_at)}</TableCell>
                    <TableCell>{p.plate ? <Plate>{p.plate}</Plate> : <Badge className="rounded-sm bg-violet-600 text-white border-transparent text-[9px] uppercase">Kurumsal</Badge>}</TableCell>
                    <TableCell className="text-xs" data-testid={`subpay-pay-customer-${p.id}`}>
                      {p.customer_name ? (
                        <div>
                          <div className="font-medium">{p.customer_name}</div>
                          {p.customer_phone && (
                            <div className="text-[10px] text-muted-foreground font-mono">
                              {p.customer_phone}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{p.plan_name}</TableCell>
                    <TableCell className="font-mono font-bold">{formatTL(p.amount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest">{METHOD_LABEL[p.method] || p.method}</Badge>
                    </TableCell>
                    <TableCell className="text-xs">{p.paid_by_operator_email}</TableCell>
                    {isSuperAdmin && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => deletePayment(p)}
                          data-testid={`sub-pay-delete-${p.id}`}
                          className="rounded-sm text-destructive h-8 w-8 p-0"
                          title="Süperadmin: Ödeme kaydını sil">
                          <Trash size={14} />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Payment dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogRow ? "Yenile / Ödeme Al" : "Yeni Abonelik Ödemesi"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Plaka</Label>
              <Input value={form.plate}
                     onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
                     className="rounded-sm font-plate uppercase"
                     data-testid="subpay-plate-input" />
            </div>
            <div>
              <Label>Plan</Label>
              <Select value={form.plan_id} onValueChange={onPlanChange}>
                <SelectTrigger className="rounded-sm" data-testid="subpay-plan-select">
                  <SelectValue placeholder="Plan seç..." />
                </SelectTrigger>
                <SelectContent>
                  {(plans || []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} — ₺{p.price} / {p.duration_days} gün
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tutar (₺)</Label>
                <Input type="number" step="0.01" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       className="rounded-sm font-mono" data-testid="subpay-amount-input" />
              </div>
              <div>
                <Label>Yöntem</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="subpay-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METHODS.map((m) => (
                      <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Başlangıç</Label>
                <Input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <Label>Bitiş</Label>
                <Input type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <Button data-testid="subpay-submit" onClick={submitPayment}
                    className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold">
              Ödemeyi Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
